import React, { useState } from 'react';
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
} from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import Home from '@/pages/Home';
import AgentPage from '@/pages/AgentPage';
import ConversationPage from '@/pages/ConversationPage';
import NewAgent from '@/components/page-components/project/agents/NewAgent';
import Login from '@/pages/Login';
import VerifyEmail from '@/pages/VerifyEmail';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Profile from '@/pages/Profile';
import ChangePassword from '@/pages/ChangePassword';
import Billing from '@/pages/Billing';
import Help from '@/pages/Help';
import NotFound from '@/pages/NotFound';
import DesignSystemShowcase from '@/pages/DesignSystemShowcase';
import { useAuth } from '@/context/Auth';
import { useSelector } from 'react-redux';
import { selectAgents } from '@/redux/agentsSlice';
import { Toaster } from '@/components/ui/toaster';
import { Button } from '@/components/ui/button';
import { Menu, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import MobileDrawer from '@/components/ui/mobile-drawer';
import CookieBanner from '@/components/common/CookieBanner';
import { AUTH_PUBLIC_ROUTES, AUTH_ROUTES } from '@/utils/auth';
import {
  ACCOUNT_LEGACY_ROUTES,
  ACCOUNT_ROUTE,
  isSidebarRoute as isAppSidebarRoute,
} from '@/utils/appRoutes';
import NotificationBell from './notifications/NotificationBell';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import { usePageTitle } from '@/hooks/usePageTitle';

const AgentTabRedirect: React.FC<{ tab: string }> = ({ tab }) => {
  const { agent_id } = useParams<{ agent_id: string }>();
  return <Navigate to={`/agents/${agent_id}?tab=${tab}`} replace />;
};

const Layout: React.FC = () => {
  const { user, isRecoverySession } = useAuth();
  const location = useLocation();
  const agents = useSelector(selectAgents);
  const isMobile = useIsMobile();
  const handleBack = useBackNavigation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Single owner of the notifications channel for the whole app.
  useRealtimeNotifications();

  // Layout owns every route, so it is also the one place that can name the
  // current page in the browser tab.
  usePageTitle();

  // Extract agent ID from URL and find the agent
  const agentId = location.pathname.match(/\/agents\/(\d+)/)?.[1];
  const currentAgent = agentId
    ? agents.find(agent => agent.id?.toString() === agentId)
    : null;

  const isSidebarRoute = isAppSidebarRoute(location.pathname);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const isAuthRoute = AUTH_PUBLIC_ROUTES.includes(
    location.pathname as (typeof AUTH_PUBLIC_ROUTES)[number]
  );

  if (isRecoverySession || !user || isAuthRoute) {
    return (
      <>
        <Routes>
          {isRecoverySession ? (
            <>
              <Route
                path={AUTH_ROUTES.resetPassword}
                element={<ResetPassword />}
              />
              <Route
                path="*"
                element={<Navigate to={AUTH_ROUTES.resetPassword} replace />}
              />
            </>
          ) : (
            <>
              <Route
                path={AUTH_ROUTES.login}
                element={user ? <Navigate to="/home" replace /> : <Login />}
              />
              <Route
                path={AUTH_ROUTES.verifyEmail}
                element={
                  user ? <Navigate to="/home" replace /> : <VerifyEmail />
                }
              />
              <Route
                path={AUTH_ROUTES.forgotPassword}
                element={
                  user ? <Navigate to="/home" replace /> : <ForgotPassword />
                }
              />
              <Route
                path={AUTH_ROUTES.resetPassword}
                element={
                  user ? <Navigate to="/home" replace /> : <ResetPassword />
                }
              />
              <Route
                path="*"
                element={
                  <Navigate to={user ? '/home' : AUTH_ROUTES.login} replace />
                }
              />
            </>
          )}
        </Routes>
        <Toaster />
      </>
    );
  }

  return (
    <div
      key={isMobile ? 'mobile-layout' : 'desktop-layout'}
      className="flex h-screen-dynamic bg-background"
    >
      {/* Desktop Sidebar */}
      {!isMobile && (
        <div className="relative">
          <Sidebar />
        </div>
      )}

      {/* Mobile Sidebar Drawer */}
      {isMobile && (
        <MobileDrawer
          isOpen={isSidebarOpen}
          onClose={closeSidebar}
          side="left"
          size="md"
          showCloseButton={true}
          className="lg:hidden"
        >
          <Sidebar onClose={closeSidebar} />
        </MobileDrawer>
      )}

      {/* Main Content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Mobile Header with Sidebar Toggle */}
        {isMobile && (
          <div className="bg-background border-b border-border lg:hidden">
            <div className="flex items-center justify-between p-4">
              {/* Back button or spacer on the left */}
              {!isSidebarRoute ? (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="text-foreground"
                >
                  <ChevronRight className="h-5 w-5 rotate-180" />
                </Button>
              ) : (
                <div className="w-20" />
              )}

              {/* Title in center */}
              <div className="flex-1 text-center">
                <h1 className="text-lg font-semibold text-foreground truncate px-2">
                  {currentAgent ? currentAgent.name : 'dialup'}
                </h1>
              </div>

              {/* Notifications + menu toggle on the right */}
              <div className="flex items-center">
                <NotificationBell />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleSidebar}
                  className="text-foreground"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Header */}
        {!isMobile && (
          <div className="w-full max-w-7xl mx-auto px-4">
            <Header agent={currentAgent} />
          </div>
        )}

        {/* Main Content Area */}
        <main
          className={`
          flex-1 overflow-y-auto
          [scrollbar-gutter:stable]
          ${isMobile ? 'p-4' : 'p-6 lg:p-8'}
          ${isMobile ? 'w-full' : 'w-full lg:max-w-7xl lg:mx-auto'}
          scrollbar-hide
        `}
        >
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<Home />} />
            <Route path="/agents/new" element={<NewAgent />} />
            <Route
              path="/agents/:agent_id/conversation/:conversation_id"
              element={<ConversationPage />}
            />
            <Route
              path="/agents/:agent_id/statistics"
              element={<AgentTabRedirect tab="usage" />}
            />
            <Route
              path="/agents/:agent_id/usage"
              element={<AgentTabRedirect tab="usage" />}
            />
            <Route
              path="/agents/:agent_id/contacts"
              element={<AgentTabRedirect tab="contacts" />}
            />
            <Route
              path="/agents/:agent_id/settings"
              element={<AgentTabRedirect tab="settings" />}
            />
            <Route
              path="/agents/:agent_id/conversations"
              element={<AgentTabRedirect tab="conversations" />}
            />
            <Route
              path="/agents/:agent_id/business"
              element={<AgentTabRedirect tab="business" />}
            />
            <Route path="/agents/:agent_id/*" element={<AgentPage />} />
            <Route path={ACCOUNT_ROUTE} element={<Profile />} />
            {/* Absorbed by /account; kept so bookmarks and older links
                (including the one in the password-change flow) resolve. */}
            {ACCOUNT_LEGACY_ROUTES.map(path => (
              <Route
                key={path}
                path={path}
                element={<Navigate to={ACCOUNT_ROUTE} replace />}
              />
            ))}
            <Route path="/billing" element={<Billing />} />
            {/* The forwarding guide moved up into /help itself; the old
                path is kept because it is linked from emails and bookmarks. */}
            <Route
              path="/help/forwarding"
              element={<Navigate to="/help" replace />}
            />
            <Route path="/help" element={<Help />} />
            <Route path="/design-system" element={<DesignSystemShowcase />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
      <Toaster />
      <CookieBanner />
    </div>
  );
};

export default Layout;
